import { useNavigate, useParams } from "react-router-dom";
import PageStructure, { PageContent, PageContentHeader, PageContentInner, Pane, PaneContent } from "./PageStructure.component";
import { invalidateDimensions, ReportBuilderDimensionORM, useDimension } from "@models/reportBuilder";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PipelineNode } from "@models/pipelineNode";
import { Form } from "react-bootstrap";
import PipelineNodeSelector from "@components/pipelineNodes/PipelineNodeSelector.component";
import PipelineNodeColumnSelector from "@components/pipelineNodes/PipelineNodeColumnSelector.component";
import { useRouteBlocker } from "@services/routing.service";
import SaveButton from "@components/button/SaveButton.component";
import { requireConfirmation } from "@services/alert/alert.service";
import toast from "@services/toast.service";
import { getErrorMessage } from "@services/errors.service";
import { DashboardIcon } from "@components/pipelineNodes/PipelineNodeIcon.component";
import { DraftOnly, ReadOnly } from "@components/project/DraftModeRequired.component";
import LoadingCard from "@components/card/LoadingCard.component";
import { invalidatePipelineNodes, usePipelineNodes } from "@stores/data.store";

const DimensionDetailsPage = () => {
    const { dimensionId } = useParams();
    const dimension = useDimension(dimensionId as string);
    const navigate = useNavigate();
    const pipelineNodes = usePipelineNodes();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [columnName, setColumnName] = useState('');
    const [pipelineNodeId, setPipelineNodeId] = useState('');
    const [fieldId, setFieldId] = useState('');

    // Check if this dimension references the DATE_DIMENSION node
    const isDateDimension = useMemo(() => {
        if (!pipelineNodes.data || !pipelineNodeId) {
            return false;
        }
        const dateDimensionNode = pipelineNodes.data.find(
            (node) => node.node_type === 'DATE_DIMENSION'
        );
        return dateDimensionNode && pipelineNodeId === (dateDimensionNode.id as string);
    }, [pipelineNodes.data, pipelineNodeId]);

    useEffect(() => {
        if (dimension.data) {
            setName(dimension.data.name);
            setDescription(dimension.data.description || '');
            setColumnName(dimension.data.column_name || '');
            setPipelineNodeId(dimension.data.pipeline_node_id || '');
            setFieldId(dimension.data.field_id || '');
        }
    }, [dimension.dataUpdatedAt]);

    const saveDimension = useCallback(async () => {
        try {
            await ReportBuilderDimensionORM.save({
                id: dimensionId as string,
                name,
                description,
                column_name: columnName,
                pipeline_node_id: pipelineNodeId,
                field_id: fieldId,
            });
            toast('success', 'Success', 'Dimension saved');
            invalidateDimensions();
            invalidatePipelineNodes();
            setPageDirty(false);
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        }
    }, [name, description, columnName, pipelineNodeId, fieldId, dimensionId]);

    const { pageDirty, setPageDirty } = useRouteBlocker(saveDimension);

    const deleteDimension = useCallback(async () => {
        if (!dimension.data) return;
        
        const confirmed = await requireConfirmation(
            `Are you sure you want to delete this dimension? It may be used in reports and charts.Please type the name of the dimension ("${dimension.data.name}") to continue`,
            'Confirm Deletion',
            'Delete',
            'Cancel',
            dimension.data.name
        );
        
        if (confirmed) {
            try {
                await ReportBuilderDimensionORM.deleteById(dimensionId as string);
                invalidateDimensions();
                // Also invalidate the nodes so the dimension field is no longer marked as a dimension
                invalidatePipelineNodes();
                toast('success', 'Success', 'Dimension deleted');
                navigate('/model/list/dimensions');
            } catch (err) {
                toast('danger', 'Error', getErrorMessage(err));
            }
        }
    }, [dimensionId, dimension.data, navigate]);

    if (dimension.isLoading) {
        return (
            <PageStructure>
                <PageContent>
                    <PageContentInner hasHeader>
                        <LoadingCard action="Loading dimension" />
                    </PageContentInner>
                </PageContent>
            </PageStructure>
        );
    }

    if (!dimension.data) {
        return (
            <PageStructure>
                <PageContent>
                    <PageContentInner hasHeader>
                        <div className="p-4">
                            <div className="alert alert-danger">Dimension not found</div>
                        </div>
                    </PageContentInner>
                </PageContent>
            </PageStructure>
        );
    }

    return (
        <PageStructure>
            <PageContent>
                <PageContentHeader>
                    <div className="d-flex center-vertically" style={{height: '100%'}}>
                        <div>
                            <DashboardIcon bgColor="blue" icon="mdi mdi-axis-arrow"/>
                        </div>
                        <div className="flex-1">
                            <div className="small text-muted">Dimension Details</div>
                            <h1 className="mb-0">{dimension.data?.name}</h1>
                        </div>
                        <DraftOnly>
                            <button 
                                onClick={deleteDimension} 
                                className="btn btn-outline-danger btn-lg me-1"
                                disabled={isDateDimension}
                                title={isDateDimension ? "System-managed date dimensions cannot be deleted" : ""}
                            >
                                <i className="mdi mdi-delete"></i> Delete Dimension
                            </button>
                            <SaveButton
                                className="btn-lg"
                                disabled={!pageDirty}
                                onClick={saveDimension}
                            />
                        </DraftOnly>
                    </div>
                </PageContentHeader>
                <PageContentInner hasHeader>
                    <Pane>
                        <PaneContent>
                            <div className="p-4">
                                <ReadOnly>
                                    <div className="card">
                                        <div className="card-body">
                                            <h2>
                                                <i className="mdi mdi-information-outline"></i> Configure Dimension
                                            </h2>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Dimension Name</Form.Label>
                                                <Form.Control 
                                                    className="w-100" 
                                                    value={name} 
                                                    onChange={(e) => {
                                                        setName(e.target.value);
                                                        setPageDirty(true);
                                                    }}
                                                />
                                                <Form.Text>Give your dimension a name so it's easy to find.</Form.Text>
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Description</Form.Label>
                                                <Form.Control 
                                                    as="textarea" 
                                                    className="w-100" 
                                                    value={description} 
                                                    onChange={(e) => {
                                                        setDescription(e.target.value);
                                                        setPageDirty(true);
                                                    }}
                                                />
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Column Name</Form.Label>
                                                <Form.Control 
                                                    className="w-100" 
                                                    value={columnName} 
                                                    onChange={(e) => {
                                                        setColumnName(e.target.value);
                                                        setPageDirty(true);
                                                    }}
                                                />
                                                <Form.Text>This is what will be used as the column header when this dimension is in a report. This will also be used for labeling in charts.</Form.Text>
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Select Entity</Form.Label>
                                                <PipelineNodeSelector
                                                    selectedId={pipelineNodeId}
                                                    disabled={isDateDimension}
                                                    onSelect={(newNode: PipelineNode | undefined) => {
                                                        setPageDirty(true);
                                                        if (!newNode) {
                                                            setPipelineNodeId('');
                                                            setFieldId('');
                                                        } else {
                                                            setPipelineNodeId(newNode.id as string);
                                                            // Reset field when node changes
                                                            setFieldId('');
                                                        }
                                                    }}
                                                    optionFilter={(pn) => {
                                                        return pn.node_type == 'DIMENSION' || pn.node_type == 'DATE_DIMENSION' || pn.node_type == 'BUSINESS_OBJECT';
                                                    }}
                                                />
                                                <Form.Text>
                                                    {isDateDimension 
                                                        ? "This is a system-managed date dimension. The entity and column cannot be changed."
                                                        : "Select the entity this dimension is based on."}
                                                </Form.Text>
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Select Column</Form.Label>
                                                <PipelineNodeColumnSelector
                                                    disabled={!pipelineNodeId || isDateDimension}
                                                    pipelineNodeId={pipelineNodeId}
                                                    selectedId={fieldId}
                                                    onSelect={(newField) => {
                                                        setPageDirty(true);
                                                        setFieldId(newField?.id || '');
                                                    }}
                                                />
                                                <Form.Text>
                                                    {isDateDimension
                                                        ? "This is a system-managed date dimension. The entity and column cannot be changed."
                                                        : "Select the column from the entity to use for this dimension."}
                                                </Form.Text>
                                            </Form.Group>
                                        </div>
                                    </div>
                                </ReadOnly>
                            </div>
                        </PaneContent>
                    </Pane>
                </PageContentInner>
            </PageContent>
        </PageStructure>
    );
}

export default DimensionDetailsPage;

